import React, { useMemo, useState } from "react";
import { Edit, Plus, Trash2 } from "lucide-react";

import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import Loader from "../ui/loader";
import AmountEntryDialog, { AmountEntryValues } from "./AmountEntryDialog";
import ConfirmAlertDialog from "./ConfirmAlertDialog";
import { useToast } from "../../hooks/use-toast";
import {
  useAddVehicleExpense,
  useDeleteVehicleExpense,
  useUpdateVehicleExpense,
  useVehicleExpenses,
} from "../../queries/vehicleExpenses.queries";
import {
  EXPENSE_CATEGORY_LABELS,
  ExpenseCategory,
  VehicleExpense,
  VehicleStatus,
} from "../../types";
import {
  formatAmount,
  formatShortDate,
  profitColor,
} from "../../lib/utils";
import { getErrorMessage } from "../../utils/general/errors";

interface VehicleExpensesPanelProps {
  vehicleId: string;
}

const SummaryItem: React.FC<{
  label: string;
  value: string;
  className?: string;
}> = ({ label, value, className }) => (
  <div className="p-3 bg-gray-50 rounded-lg text-center">
    <p className="text-xs text-gray-600">{label}</p>
    <p className={`font-semibold ${className ?? ""}`}>{value}</p>
  </div>
);

const VehicleExpensesPanel: React.FC<VehicleExpensesPanelProps> = ({ vehicleId }) => {
  const { toast } = useToast();
  const { data, isLoading } = useVehicleExpenses(vehicleId);
  const addExpense = useAddVehicleExpense(vehicleId);
  const updateExpense = useUpdateVehicleExpense(vehicleId);
  const deleteExpense = useDeleteVehicleExpense(vehicleId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<VehicleExpense | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<VehicleExpense | null>(null);

  const initialValues = useMemo<AmountEntryValues<ExpenseCategory> | null>(
    () =>
      editing
        ? {
            category: editing.category,
            amount: editing.amount,
            expense_date: editing.expense_date,
            description: editing.description ?? undefined,
          }
        : null,
    [editing],
  );

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (expense: VehicleExpense) => {
    setEditing(expense);
    setDialogOpen(true);
  };

  const handleSubmit = async (values: AmountEntryValues<ExpenseCategory>) => {
    try {
      if (editing) {
        await updateExpense.mutateAsync({ id: editing.id, ...values });
      } else {
        await addExpense.mutateAsync(values);
      }
      toast({
        title: "Success!",
        description: editing ? "Expense updated" : "Expense added",
      });
      setDialogOpen(false);
      setEditing(null);
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to save expense"),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteExpense.mutateAsync(deleteTarget.id);
      toast({ title: "Success!", description: "Expense removed" });
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to remove expense"),
        variant: "destructive",
      });
    } finally {
      setDeleteTarget(null);
    }
  };

  if (isLoading || !data) {
    return <Loader isSmall />;
  }

  const { expenses, summary } = data;
  const isSold = summary.status === VehicleStatus.SOLD || summary.price_sold != null;
  const profitValue = isSold ? summary.profit : summary.projected_profit;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <SummaryItem label="Bought Price" value={formatAmount(summary.price_bought)} />
        <SummaryItem label="Total Expenses" value={formatAmount(summary.total_expenses)} />
        <SummaryItem label="Total Cost" value={formatAmount(summary.total_cost)} />
        <SummaryItem
          label={isSold ? "Sold Price" : "Listing Price"}
          value={
            isSold
              ? summary.price_sold != null
                ? formatAmount(summary.price_sold)
                : "Not set"
              : formatAmount(summary.listing_price)
          }
        />
        <SummaryItem
          label={isSold ? "Profit" : "Projected Profit"}
          value={profitValue != null ? formatAmount(profitValue) : "-"}
          className={profitColor(profitValue)}
        />
        <SummaryItem
          label="Status"
          value={
            isSold
              ? `Sold${summary.sold_at ? ` (${formatShortDate(summary.sold_at)})` : ""}`
              : "Available"
          }
        />
      </div>

      <div className="flex items-center justify-between">
        <h4 className="font-semibold">Expenses ({expenses.length})</h4>
        <Button size="sm" onClick={openAdd}>
          <Plus className="mr-2 h-4 w-4" />
          Add Expense
        </Button>
      </div>

      {expenses.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">
          No expenses recorded for this vehicle yet.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="hidden sm:table-cell">Description</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {expenses.map((expense) => (
              <TableRow key={expense.id}>
                <TableCell className="whitespace-nowrap">
                  {formatShortDate(expense.expense_date)}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {EXPENSE_CATEGORY_LABELS[expense.category] ?? expense.category}
                  </Badge>
                </TableCell>
                <TableCell className="hidden sm:table-cell max-w-[200px] truncate">
                  {expense.description || "-"}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {formatAmount(expense.amount)}
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Edit expense"
                      onClick={() => openEdit(expense)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label="Delete expense"
                      onClick={() => setDeleteTarget(expense)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AmountEntryDialog
        open={dialogOpen}
        title={editing ? "Edit Expense" : "Add Expense"}
        description="Record a cost incurred for this vehicle"
        categoryLabels={EXPENSE_CATEGORY_LABELS}
        defaultCategory={ExpenseCategory.REPAIR}
        initialValues={initialValues}
        isSubmitting={addExpense.isPending || updateExpense.isPending}
        onSubmit={handleSubmit}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
      />

      <ConfirmAlertDialog
        open={!!deleteTarget}
        title="Delete expense"
        message="This expense will be permanently removed and profit will be recalculated."
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default VehicleExpensesPanel;
